// JSONEditorSOPanel.js
import React, { useEffect, useMemo, useState } from "react";
import "./JSONEditorSOPanel.css"; // reuse existing styles

/**
 * JSONEditorSOPanel
 * Shared JSON editor for:
 *  - control.attributes
 *  - full control JSON
 *  - full form JSON (if wired)
 *
 * Props:
 *  - initialObject: object | string | any
 *  - onApply(parsedObject)
 *  - onCancel()           (optional; shows Cancel button if provided)
 *  - applyLabel           (optional, default "Apply")
 *  - noChangesLabel       (optional, default "No changes")
 *  - unsavedLabel         (optional, default "Unsaved changes")
 *  - errorPrefix          (optional, default "JSON error")
 */
export default function JSONEditorSOPanel({
  initialObject = {},
  onApply,
  onCancel,
  applyLabel = "Apply",
  noChangesLabel = "No changes",
  unsavedLabel = "Unsaved changes",
  errorPrefix = "JSON error",
}) {
  /**
   * Normalise the incoming value *once per change of initialObject*.
   * We derive:
   *  - initialPretty: the canonical pretty-printed string we show initially
   *  - initialComparable: the value used for semantic change detection
   */
  const { initialPretty, initialComparable } = useMemo(() => {
    try {
      let value = initialObject;

      // If the incoming value is a string, try to parse as JSON.
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === "object") {
            return {
              initialPretty: JSON.stringify(parsed, null, 2),
              initialComparable: parsed,
            };
          }
          // Parsed but not object/array – treat as plain string
          return {
            initialPretty: String(value),
            initialComparable: String(value),
          };
        } catch {
          // Not valid JSON – treat as literal string
          return {
            initialPretty: String(value),
            initialComparable: String(value),
          };
        }
      }

      // If it's an object/array, pretty-print it
      if (value && typeof value === "object") {
        return {
          initialPretty: JSON.stringify(value, null, 2),
          initialComparable: value,
        };
      }

      // Fallback: numbers, booleans, null, undefined…
      const asString = String(value ?? "{}");
      return {
        initialPretty: asString,
        initialComparable: asString,
      };
    } catch {
      return {
        initialPretty: "{}",
        initialComparable: {},
      };
    }
  }, [initialObject]);

  const [text, setText] = useState(initialPretty);
  const [error, setError] = useState("");
  const [canFormat, setCanFormat] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset textarea when the underlying initialObject changes
  useEffect(() => {
    setText(initialPretty);
    setError("");
    setCanFormat(false);
    setHasChanges(false);
  }, [initialPretty]);

  /**
   * Live validation + status:
   *  - parses JSON on every change
   *  - sets error message if invalid
   *  - decides if formatting is needed (text != pretty-printed)
   *  - decides if semantic changes exist (parsed != initialComparable)
   */
  useEffect(() => {
    try {
      const parsed = JSON.parse(text);
      setError("");

      // Whether the current text is already in canonical pretty format
      const pretty = JSON.stringify(parsed, null, 2);
      setCanFormat(text !== pretty);

      // Semantic change detection
      if (initialComparable && typeof initialComparable === "object") {
        const a = JSON.stringify(initialComparable);
        const b = JSON.stringify(parsed);
        setHasChanges(a !== b);
      } else {
        // Fallback for non-object initial values
        setHasChanges(text !== initialPretty);
      }
    } catch (e) {
      setError(e?.message || "Invalid JSON");
      setCanFormat(false);
      setHasChanges(false);
    }
  }, [text, initialComparable, initialPretty]);

  const handleFormat = () => {
    if (!canFormat || error) return;
    try {
      const parsed = JSON.parse(text);
      const pretty = JSON.stringify(parsed, null, 2);
      setText(pretty);
      // After this, effect will recompute canFormat/hasChanges.
    } catch {
      // If it throws here, something changed between validation and click; ignore.
    }
  };

  const handleApply = () => {
    if (!onApply || error || !hasChanges) return;

    try {
      const parsed = JSON.parse(text);
      onApply(parsed);

      // Normalise the editor content to canonical format after apply.
      const pretty = JSON.stringify(parsed, null, 2);
      setText(pretty);
      // The parent will usually update initialObject; when that happens,
      // the effect above will reset hasChanges to false.
    } catch {
      // Parsing failed between validation and click – treat as no-op.
    }
  };

  const applyDisabled = !!error || !hasChanges;
  const formatDisabled = !!error || !canFormat;

  const statusNode = error ? (
    <span className="fe-error" role="alert">
      {errorPrefix}: {error}
    </span>
  ) : (
    <span className="fe-hint">
      {hasChanges ? unsavedLabel : noChangesLabel}
    </span>
  );

  return (
    <div className="fe-attrpanel">
      <textarea
        className={`fe-textarea ${error ? "has-error" : ""}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="{}"
        spellCheck={false}
      />

      <div className="fe-attrpanel__footer">
        <div className="fe-attrpanel__status">{statusNode}</div>

        <div className="fe-attrpanel__actions">
          <button
            type="button"
            className={`fe-btn fe-btn--ghost fe-btn--format${
              formatDisabled ? " is-disabled" : ""
            }`}
            onClick={handleFormat}
            disabled={formatDisabled}
            title={
              error
                ? "Fix JSON errors before formatting"
                : canFormat
                ? "Pretty format JSON"
                : "Already formatted"
            }
          >
            Format
          </button>

          {onCancel && (
            <button
              type="button"
              className="fe-btn fe-btn--ghost"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}

          <button
            type="button"
            className="fe-btn fe-btn--primary"
            onClick={handleApply}
            disabled={applyDisabled}
            title={applyDisabled ? "No changes to apply" : "Apply JSON"}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
